/**
 * Copyright 2025 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */

// Typs for OPC UA PubSub Messages
export type GUIDStr = string;
export type MessageTypes = 'ua-metadata' | 'ua-data';

export enum NodeClassType {
  OBJECT = 1,
  VARIABLE = 2,
  METHOD = 4,
  OBJECT_TYPE = 8,
  VARIABLE_TYPE = 16,
  REFERENCE_TYPE = 32,
  DATA_TYPE = 64,
  VIEW = 128,
}

export interface Frame {
  MessageId: GUIDStr;
  MessageType: MessageTypes;
  PublisherId: number;
}

export interface LocalizedText {
  Text: string;
  Locale?: string;
}

export interface QualifiedName {
  Name: string;
  NamespaceIndex?: number;
  Uri?: number;
}

export type NodeIdIdentifierType = number | string | GUIDStr;

export interface NodeId {
  Id: NodeIdIdentifierType;
  Namespace?: number;
}

export interface MetadataFieldChild {
  Name: string;
  FieldFlags: number;
  BuiltInType: number;
  DataType: NodeId;
  ValueRank: number;
  MaxStringLength: number;
  DataSetFieldId: GUIDStr;
}

export interface ReferenceDescription {
  ReferenceTypeId: NodeId;
  IsForward: boolean;
  NodeId: NodeId;
  BrowseName: QualifiedName;
  DisplayName: LocalizedText;
  NodeClass: NodeClassType;
}

export interface MetadataReference {
  Key: {
    Name: string;
  };
  Value: {
    Type: 22; // structure
    Body: {
      TypeId: {
        Id: 518; //ReferenceDescription
      };
      Body: ReferenceDescription;
    };
  };
}

export interface MetadataFieldReferences extends MetadataFieldChild {
  Name: 'virtualId';
  Description: {
    Text: string;
  };
  Properties: MetadataReference[];
}

export type MetadataFieldTypes = MetadataFieldChild | MetadataFieldReferences;

export type MetadataEnumDataTypes = any;

export interface MetadataFrame extends Frame {
  MessageType: 'ua-metadata';
  DataSetWriterId: number;
  MetaData: {
    Name: string;
    Namespaces: string[];
    EnumDataTypes: MetadataEnumDataTypes[];
    Description: {
      Text: string;
    };
    ConfigurationVersion: {
      MajorVersion: number;
      MinorVersion: number;
    };
    Fields: MetadataFieldTypes[];
  };
}

export type ValueType = any;

export interface DataFrame extends Frame {
  MessageType: 'ua-data';
  Messages: {
    Payload: {
      [key: string]: ValueType;
    };
  }[];
}

function defaultToZero(v?: number): number {
  return v === undefined ? 0 : v;
}

export function CompareNodeId(nId1: NodeId, nId2: NodeId) {
  return nId1.Id == nId2.Id && defaultToZero(nId1.Namespace) === defaultToZero(nId2.Namespace);
}

export function CompareQualifiedName(qn1: QualifiedName, qn2: QualifiedName) {
  return qn1.Name == qn2.Name && defaultToZero(qn1.NamespaceIndex) === defaultToZero(qn2.NamespaceIndex);
}

/// Additional Types

export interface ExpandedNodeId {
  serverIndex?: number; // undefined is equal to serverIndex == 0
  namespaceUri: string;
  identifier: number | string | GUIDStr;
}
